import React from 'react';
import { Link } from 'react-router-dom';
import { Info, Database, MapPin, MessageCircle, Trophy } from 'lucide-react';
import Hero from '../components/ui/Hero';
import ScrollReveal from '../components/ui/ScrollReveal';

const AboutUsPage = () => {
  const heroImage = "https://nifmkzddenyukgjcdetc.supabase.co/storage/v1/object/public/animatronics_data/logo_light.webp";

  return (
    <div>
      <Hero
        title="About Animatronic Database"
        description="Learn about our mission and the team behind the world's most comprehensive animatronic resource."
        backgroundImage={heroImage}
        compact
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <ScrollReveal>
              <div className="card p-8 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-brand/10 rounded-lg">
                    <Info className="w-6 h-6 text-brand" />
                  </div>
                  <h2 className="text-2xl font-bold text-content-primary">Our Mission</h2>
                </div>
                
                <div className="prose prose-invert max-w-none space-y-6">
                  <p>
                    The Animatronic Database is a hub for animatronic enthusiasts, industry professionals, and theme park fans. Our mission is to celebrate the art and engineering of animatronics by compiling a growing, comprehensive database of animatronic characters from around the world.
                  </p>
                  <p>
                    With a focus on animatronics in theme parks, museums, exhibitions, and live attractions, we aim to provide a platform that connects fans with the stories and innovations behind these incredible creations. While TV and film animatronics are undeniably impressive, our primary goal is to highlight animatronics designed for interactive and immersive experiences.
                  </p>
                  <p>
                    New entries are continuously added to the database, and as we grow, our ambition is to become the largest collection of animatronic information available online. If you're joining us early in our journey, you'll notice fewer entries, but rest assured—our database is expanding rapidly, with nearly one new animatronic added every day!
                  </p>
                  <p>
                    We also recognize and respect the creators and media owners behind the animatronics we feature. The images, videos, and information in our database are collected under transformative fair use, and we strive to credit the rightful owners wherever possible. If you have any concerns or corrections, we encourage you to reach out to us directly.
                  </p>
                  <p>
                    Explore, learn, and connect—whether you're a casual fan, a hobbyist, or a professional, The Animatronic Database is your ultimate resource for all things animatronic.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="card p-8">
                <h2 className="text-2xl font-bold text-content-primary mb-6">Our Features</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex flex-col h-full">
                    <div className="p-2 bg-brand/10 rounded-lg w-fit mb-4">
                      <Database className="w-6 h-6 text-brand" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Comprehensive Database</h3>
                    <p className="text-content-secondary flex-grow">
                      Browse our extensive collection of animatronic figures with detailed information, media, and technical specifications.
                    </p>
                    <Link to="/database" className="mt-4 text-brand hover:text-brand-light">
                      Explore the Database →
                    </Link>
                  </div>

                  <div className="flex flex-col h-full">
                    <div className="p-2 bg-brand/10 rounded-lg w-fit mb-4">
                      <MapPin className="w-6 h-6 text-brand" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Interactive Map</h3>
                    <p className="text-content-secondary flex-grow">
                      Visualize animatronic locations worldwide and plan your next theme park adventure.
                    </p>
                    <Link to="/map" className="mt-4 text-brand hover:text-brand-light">
                      View the Map →
                    </Link>
                  </div>

                  <div className="flex flex-col h-full">
                    <div className="p-2 bg-brand/10 rounded-lg w-fit mb-4">
                      <MessageCircle className="w-6 h-6 text-brand" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Community Forum</h3>
                    <p className="text-content-secondary flex-grow">
                      Connect with fellow enthusiasts, share knowledge, and discuss your favorite animatronics.
                    </p>
                    <Link to="/community/forum" className="mt-4 text-brand hover:text-brand-light">
                      Join the Discussion →
                    </Link>
                  </div>

                  <div className="flex flex-col h-full">
                    <div className="p-2 bg-brand/10 rounded-lg w-fit mb-4">
                      <Trophy className="w-6 h-6 text-brand" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Ranking Game</h3>
                    <p className="text-content-secondary flex-grow">
                      Vote for your favorite animatronics and see how they rank against others in the community.
                    </p>
                    <Link to="/ranking-game" className="mt-4 text-brand hover:text-brand-light">
                      Start Ranking →
                    </Link>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>

          <div className="lg:col-span-1">
            <ScrollReveal>
              <div className="card p-8 sticky top-24">
                <h2 className="text-2xl font-bold text-content-primary mb-6">Contact Us</h2>
                <p className="text-content-secondary mb-6">
                  Have questions, suggestions, or want to contribute to the database? We'd love to hear from you!
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-brand hover:bg-brand-dark text-white rounded-lg transition-colors w-full justify-center"
                >
                  Get in Touch
                </Link>

                <div className="mt-8">
                  <h3 className="text-xl font-semibold mb-4">Quick Links</h3>
                  <ul className="space-y-2">
                    <li>
                      <Link to="/contact" className="text-content-secondary hover:text-brand transition-colors">
                        Contact Us
                      </Link>
                    </li>
                    <li>
                      <Link to="/advertising" className="text-content-secondary hover:text-brand transition-colors">
                        Advertising Opportunities
                      </Link>
                    </li>
                    <li>
                      <Link to="/legal/privacy" className="text-content-secondary hover:text-brand transition-colors">
                        Privacy Policy
                      </Link>
                    </li>
                    <li>
                      <Link to="/legal/terms" className="text-content-secondary hover:text-brand transition-colors">
                        Terms of Service
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUsPage;